import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { PRODUCTS_QUERY, storefrontApiRequest, type ShopifyProduct } from "@/lib/shopify";

export const productsQueryOptions = {
  queryKey: ["products"] as const,
  queryFn: async () => {
    const res = await storefrontApiRequest(PRODUCTS_QUERY, { first: 100, query: null });
    return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
  },
};

export type CategoryFAQ = { q: string; a: string };

export interface CategoryLandingProps {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  body: { heading: string; paragraphs: string[] }[];
  faqs: CategoryFAQ[];
  kauppaCategory: string;
  match: (title: string, vendor?: string, tags?: string[]) => boolean;
  relatedChips?: Array<{ label: string; to: string }>;
  productChips?: Array<{ label: string; handle: string }>;
  cta?: {
    heading: string;
    body: string;
    primaryLabel?: string;
    secondaryLabel?: string;
    secondaryTo?: string;
  };
}

export function CategoryLanding({
  slug,
  eyebrow,
  title,
  intro,
  body,
  faqs,
  kauppaCategory,
  match,
  relatedChips,
  productChips,
  cta,
}: CategoryLandingProps) {
  const { data } = useSuspenseQuery(productsQueryOptions);
  const products = (data ?? []).filter((p) => {
    const n = p.node;
    const vendor = (n as unknown as { vendor?: string }).vendor;
    const tags = (n as unknown as { tags?: string[] }).tags;
    return match(n.title, vendor, tags);
  });

  return (
    <Layout>
      <section className="container-prose pt-14 pb-8 md:pt-20 md:pb-12">
        <nav aria-label="Murupolku" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Etusivu</Link>
          <span className="text-border">/</span>
          <Link to="/kauppa" className="hover:text-foreground transition-colors">Kauppa</Link>
          <span className="text-border">/</span>
          <span className="text-foreground">{eyebrow}</span>
        </nav>

        <div className="mt-6 md:mt-8 max-w-3xl">
          <span className="block text-[11px] font-medium uppercase tracking-[0.25em] text-clay mb-3">{eyebrow}</span>
          <h1 className="text-4xl md:text-6xl text-foreground text-balance leading-[1.05]">{title}</h1>
          <p className="mt-4 md:mt-5 max-w-2xl text-[15px] md:text-base text-muted-foreground leading-relaxed">{intro}</p>
        </div>

        {relatedChips && relatedChips.length > 0 && (
          <div className="mt-7 md:mt-8 flex flex-wrap gap-2">
            {relatedChips.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="rounded-full border border-border bg-surface px-4 py-2 text-[13px] text-foreground/80 hover:border-foreground/40 hover:text-foreground transition-colors"
              >
                {c.label}
              </Link>
            ))}
          </div>
        )}
        {productChips && productChips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-clay">Suositut mallit</span>
            {productChips.map((c) => (
              <Link
                key={c.handle}
                to="/product/$handle"
                params={{ handle: c.handle }}
                className="rounded-full bg-foreground px-4 py-1.5 text-sm text-background hover:opacity-90"
              >
                {c.label} →
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="container-prose pb-16 md:pb-24">
        <div className="mb-8 md:mb-10 flex items-end justify-between gap-4 border-b border-border pb-4">
          <p className="text-[12px] uppercase tracking-[0.2em] text-muted-foreground">
            {products.length} {products.length === 1 ? "tuote" : "tuotetta"}
          </p>
          <Link
            to="/kauppa"
            search={{ kategoria: kauppaCategory } as never}
            className="text-[12px] font-medium uppercase tracking-[0.15em] text-foreground hover:text-clay transition-colors"
          >
            Selaa koko valikoimaa →
          </Link>
        </div>

        {products.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-cream py-16 text-center">
            <p className="text-foreground">Tähän kategoriaan tulossa uusia tuotteita pian.</p>
            <Link to="/kauppa" className="mt-4 inline-block text-sm text-foreground underline underline-offset-4">
              Selaa koko kauppaa
            </Link>
          </div>
        ) : (
          <>
            <div className="grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.node.id} product={p} />
              ))}
            </div>
            <div className="mt-14 md:mt-16 text-center">
              <Link
                to="/kauppa"
                search={{ kategoria: kauppaCategory } as never}
                className="inline-flex items-center rounded-full border border-border bg-surface px-7 py-3.5 text-sm text-foreground hover:border-foreground/40 transition-colors"
              >
                Selaa koko valikoimaa →
              </Link>
            </div>
          </>
        )}
      </section>

      {cta && (
        <section className="border-t border-border bg-foreground text-background">
          <div className="container-prose py-16 md:py-20 text-center">
            <h2 className="text-3xl md:text-4xl text-background text-balance">{cta.heading}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-background/80 leading-relaxed">{cta.body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/kauppa"
                search={{ kategoria: kauppaCategory } as never}
                className="inline-flex items-center rounded-full bg-background px-6 py-3 text-sm font-medium text-foreground hover:opacity-90"
              >
                {cta.primaryLabel ?? "Tutustu mallistoon"} →
              </Link>
              {cta.secondaryTo && (
                <Link
                  to={cta.secondaryTo}
                  className="inline-flex items-center rounded-full border border-background/40 px-6 py-3 text-sm text-background hover:border-background"
                >
                  {cta.secondaryLabel ?? "Lue opas"}
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-border bg-cream/40">
        <div className="container-prose section-y">
          <div className="max-w-3xl">
            {body.length > 0 && (
              <>
                <span className="block text-[11px] font-medium uppercase tracking-[0.25em] text-clay mb-3">Lue lisää</span>
                <h2 className="text-3xl md:text-4xl text-foreground text-balance leading-[1.1]">{body[0]!.heading}</h2>
                <div className="mt-5 space-y-4 text-[15px] leading-[1.7] text-muted-foreground">
                  {body[0]!.paragraphs.slice(0, 2).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </>
            )}

            {(body.length > 1 || body[0]!.paragraphs.length > 2) && (
              <div className="mt-12 divide-y divide-border border-y border-border">
                {body[0]!.paragraphs.length > 2 && (
                  <details className="group py-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-foreground">
                      <span className="font-display">Lue lisää — {body[0]!.heading.toLowerCase()}</span>
                      <span className="text-muted-foreground transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <div className="mt-4 space-y-4 text-[15px] leading-[1.7] text-muted-foreground">
                      {body[0]!.paragraphs.slice(2).map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  </details>
                )}
                {body.slice(1).map((b) => (
                  <details key={b.heading} className="group py-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-foreground">
                      <span className="font-display">{b.heading}</span>
                      <span className="text-muted-foreground transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <div className="mt-4 space-y-4 text-[15px] leading-[1.7] text-muted-foreground">
                      {b.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {faqs.length > 0 && (
              <div className="mt-16 md:mt-20">
                <span className="block text-[11px] font-medium uppercase tracking-[0.25em] text-clay mb-3">Usein kysyttyä</span>
                <h2 className="text-3xl md:text-4xl text-foreground text-balance leading-[1.1]">Usein kysyttyä — {eyebrow.toLowerCase()}</h2>
                <div className="mt-6 divide-y divide-border border-y border-border">
                  {faqs.map((f) => (
                    <details key={f.q} className="group py-5">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-foreground">
                        <span className="font-display pr-4">{f.q}</span>
                        <span className="text-muted-foreground transition-transform duration-300 group-open:rotate-45 shrink-0">+</span>
                      </summary>
                      <p className="mt-3 text-[15px] leading-[1.7] text-muted-foreground">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <p className="sr-only">Sivun URL: https://montessori24.fi/{slug}</p>
    </Layout>
  );
}

export function categoryHead({
  slug,
  title,
  h1,
  description,
  faqs,
  image,
}: {
  slug: string;
  title: string;
  h1: string;
  description: string;
  faqs: CategoryFAQ[];
  image?: string;
}) {
  const url = `https://montessori24.fi/${slug}`;
  const ogImage = image ?? "https://montessori24.fi/og-default.jpg";
  const faqLd = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: h1,
    url,
    inLanguage: "fi-FI",
    description,
    isPartOf: {
      "@type": "WebSite",
      name: "Montessori24",
      url: "https://montessori24.fi/",
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
      { "@type": "ListItem", position: 2, name: "Kauppa", item: "https://montessori24.fi/kauppa" },
      { "@type": "ListItem", position: 3, name: h1, item: url },
    ],
  };
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:url", content: url },
      { property: "og:image", content: ogImage },
      { property: "og:image:alt", content: h1 },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", hrefLang: "fi", href: url },
      { rel: "alternate", hrefLang: "x-default", href: url },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(collectionLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ...(faqLd ? [{ type: "application/ld+json", children: JSON.stringify(faqLd) }] : []),
    ],
  };
}
